import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { moveForwardServiceAgreement } from 'thunks/accountServiceAgreementsThunk';
import ProductApproval from './ProductApproval';
import React, { useCallback } from 'react';
import { Box, Button, Grid } from '@mui/material';
import CustomStepMetadata from 'components/customStepMetadata/CustomStepMetadata';
import CommonDialog from 'components/common/CommonDialog';
import CloseIcon from 'assets/icons/close-icon.svg?react';
import AddDocument from 'assets/icons/add-document-icon.svg?react';
import { jbColors } from 'theme/jbColors';
import { getAction, getStepMetadata } from 'utils/json-form/formDataUtils';
import { StepMetadata } from 'pco-product-service-internal-consumer';
import { ServiceRole } from 'pco-product-service-internal-consumer';
import { setOpenedTaskUid } from 'reducers/serviceAgreementsReducer';
import { StepMetadataType } from 'types';

type Props = {
  taskUid?: string;
  stepMetadata: StepMetadata | undefined;
  selectedServiceRole?: ServiceRole;
  isServiceRoleDetails?: boolean;
  textToDisplay?: string;
};

const OpenTask = ({ taskUid, stepMetadata, selectedServiceRole, isServiceRoleDetails, textToDisplay }: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const [open, setOpen] = React.useState(!!stepMetadata?.workAccess);
  const [prevStepMetadata, setPrevStepMetadata] = React.useState(stepMetadata);
  if (stepMetadata !== prevStepMetadata) {
    setPrevStepMetadata(stepMetadata);
    setOpen(!!stepMetadata?.workAccess);
  }
  const selectedServiceAgreement = useSelector((state: RootState) => state.serviceAgreements.selectedServiceAgreement);
  const error = useSelector((state: RootState) => state.appConfig.error);

  const handleOnSubmit = useCallback(
    (formData: Record<string, unknown>) => {
      if (!selectedServiceAgreement) {
        return;
      }

      const action = getAction(formData);
      const stringifiedFormData = JSON.stringify(formData);
      dispatch(
        moveForwardServiceAgreement(
          taskUid,
          selectedServiceAgreement?.serviceAgreementId ?? '',
          stringifiedFormData,
          action,
        ),
      );

      setOpen(false);
    },
    [dispatch, selectedServiceAgreement, taskUid],
  );

  const styles = {
    button: {
      padding: 0,
      minWidth: '94px',
      backgroundColor: 'white',
      ':hover': {
        backgroundColor: jbColors.core.reflexBlue[100],
        color: 'white',
      },
    },
  };

  const handleOpenTask = () => {
    setOpen(true);
    dispatch(setOpenedTaskUid(stepMetadata?.techId));
  };
  const taskNamefromStepMetadata = stepMetadata?.taskName;

  return (
    <Box data-testid="open-task" sx={{ display: 'flex' }}>
      <Grid
        container
        sx={{
          display: 'flex',
          padding: '1em',
          border: '1px solid blue',
          backgroundColor: '#EAF2FF',
          marginTop: '0.5em',
        }}
      >
        {stepMetadata?.stepMetadataType !== StepMetadataType.INFORMATION_METADATA && (
          <Grid item xs={8} sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
            {/* <Grid>Open task on this product: {stepMetadata?.taskName}</Grid> */}
            <Grid>{textToDisplay ?? `Open task on this product: ${stepMetadata?.taskName}`}</Grid>
          </Grid>
        )}
        <Grid
          item
          xs={4}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexDirection: 'row',
          }}
        >
          {stepMetadata?.stepMetadataType !== StepMetadataType.INFORMATION_METADATA && (
            <Button
              id="work-on-task"
              sx={[styles.button, { border: '1px solid blue' }]}
              onClick={handleOpenTask}
              disabled={!stepMetadata?.workAccess}
            >
              Work on task
            </Button>
          )}
        </Grid>
        <Grid container>
          {stepMetadata?.stepMetadataType === StepMetadataType.INFORMATION_METADATA && stepMetadata.message && (
            <div dangerouslySetInnerHTML={{ __html: stepMetadata.message }} />
          )}
        </Grid>
      </Grid>
      <CommonDialog
        isOpen={open && !error.open}
        sx={{ maxWidth: '60em' }}
        onClose={() => setOpen(false)}
        title={taskNamefromStepMetadata}
        titleIcon={<AddDocument />}
        closeIcon={<CloseIcon style={{ fill: jbColors.core.reflexBlue[100] }} onClick={() => setOpen(false)} />}
        removeCancel={true}
        content={
          stepMetadata?.stepMetadataType === StepMetadataType.CUSTOM_COMPONENT ? (
            <CustomStepMetadata
              isReadOnly={false}
              customStepMetadata={getStepMetadata(
                !isServiceRoleDetails ? selectedServiceAgreement : selectedServiceRole,
                StepMetadataType.CUSTOM_COMPONENT,
              )}
              closeAction={() => setOpen(false)}
            />
          ) : (
            <ProductApproval
              attributes={getStepMetadata(
                !isServiceRoleDetails ? selectedServiceAgreement : selectedServiceRole,
                StepMetadataType.PREAPPROVAL_FORM_METADATA,
              )}
              hasHandleCancel={false}
              handleCancel={() => undefined}
              handleSubmit={handleOnSubmit}
            />
          )
        }
      />
    </Box>
  );
};

export default OpenTask;
